import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';
import { qualifiantePrograms, diplomatePrograms, certifiantePrograms, linguistiquePrograms } from '../data/programs';

const centers = [
  { id: 'diplomante', name: 'Centre de Formation Diplômante', color: '#003366' },
  { id: 'qualifiante', name: 'Centre de Formation Qualifiante', color: '#DC143C' },
  { id: 'certifiante', name: 'Centre de Formation Certifiante', color: '#FFD700' },
  { id: 'linguistique', name: 'Centre de Formation Linguistique', color: '#8B5CF6' },
];

const getProgramsByCenter = (centerId) => {
  switch (centerId) {
    case 'diplomante':
      return [
        ...diplomatePrograms.bachelor.map(p => ({ id: p.id, name: p.name })),
        ...diplomatePrograms.master.map(p => ({ id: p.id, name: p.name }))
      ];
    case 'qualifiante':
      return qualifiantePrograms.map(p => ({ id: p.id, name: p.name }));
    case 'certifiante':
      return certifiantePrograms.map(p => ({ id: p.id, name: p.name }));
    case 'linguistique':
      return linguistiquePrograms.map(p => ({ id: p.id, name: p.name }));
    default:
      return [];
  }
};

export default function PreInscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCenter = searchParams.get('center') || '';
  const initialProgram = searchParams.get('program') || '';

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    center: initialCenter,
    program: initialProgram,
    education_level: '',
    motivation: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const programs = getProgramsByCenter(formData.center);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.center || !formData.program) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/preregistrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Erreur lors de la soumission');

      setSuccess(true);
      toast.success('Préinscription envoyée avec succès !');
    } catch (error) {
      console.error(error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="preinscription-success">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-lg"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={40} />
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#003366] mb-4">
            Préinscription réussie !
          </h1>
          <p className="text-gray-600 mb-8">
            Merci pour votre préinscription. Notre équipe vous contactera dans les plus brefs délais 
            pour confirmer votre candidature et vous fournir les informations complémentaires.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/')} variant="outline" className="rounded-full">
              <ArrowLeft size={18} className="mr-2" /> Retour à l'accueil
            </Button>
            <Button 
              onClick={() => {
                setSuccess(false);
                setFormData({
                  first_name: '',
                  last_name: '',
                  email: '',
                  phone: '',
                  center: '',
                  program: '',
                  education_level: '',
                  motivation: ''
                });
              }} 
              className="bg-[#003366] hover:bg-[#002244] rounded-full"
            >
              Nouvelle préinscription
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12" data-testid="preinscription-page">
      <SEO
        title="Preinscription en Ligne - CEFAT Business School"
        description="Preinscrivez-vous en ligne a CEFAT Business School Abidjan. Bachelor, MBA, formations qualifiantes et certifiantes. Processus rapide et simple."
        keywords="preinscription CEFAT, inscription ecole commerce Abidjan, s'inscrire CEFAT, admission MBA Abidjan, inscription Bachelor Cote d'Ivoire"
        url="/preinscription"
      />
      {/* Hero */}
      <div className="bg-[#003366] py-16 mb-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-3xl md:text-5xl font-bold text-white mb-4"
          >
            Préinscription en ligne
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-lg max-w-2xl mx-auto"
          >
            Remplissez le formulaire ci-dessous pour démarrer votre parcours vers l'excellence avec CEFAT Business School.
          </motion.p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4">
        <motion.form 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          <div className="grid md:grid-cols-2 gap-6">
            {/* Prénom */}
            <div>
              <Label htmlFor="first_name" className="text-[#003366] font-semibold">
                Prénom <span className="text-red-500">*</span>
              </Label>
              <Input
                id="first_name"
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="mt-2 h-12 border-gray-200 focus:border-[#003366] focus:ring-[#003366]/20"
                placeholder="Votre prénom"
                data-testid="input-first-name"
              />
            </div>

            {/* Nom */}
            <div>
              <Label htmlFor="last_name" className="text-[#003366] font-semibold">
                Nom <span className="text-red-500">*</span>
              </Label>
              <Input
                id="last_name"
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="mt-2 h-12 border-gray-200 focus:border-[#003366] focus:ring-[#003366]/20"
                placeholder="Votre nom"
                data-testid="input-last-name"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-[#003366] font-semibold">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2 h-12 border-gray-200 focus:border-[#003366] focus:ring-[#003366]/20"
                placeholder="votre@email.com"
                data-testid="input-email"
              />
            </div>

            {/* Téléphone */}
            <div>
              <Label htmlFor="phone" className="text-[#003366] font-semibold">
                Téléphone <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-2 h-12 border-gray-200 focus:border-[#003366] focus:ring-[#003366]/20"
                placeholder="+225 07 00 00 00 00"
                data-testid="input-phone"
              />
            </div>

            {/* Centre */}
            <div>
              <Label className="text-[#003366] font-semibold">
                Centre <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.center} 
                onValueChange={(value) => setFormData({ ...formData, center: value, program: '' })}
              >
                <SelectTrigger className="mt-2 h-12 border-gray-200" data-testid="select-center">
                  <SelectValue placeholder="Choisir un centre" />
                </SelectTrigger>
                <SelectContent>
                  {centers.map((center) => (
                    <SelectItem key={center.id} value={center.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: center.color }} />
                        {center.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Programme */}
            <div>
              <Label className="text-[#003366] font-semibold">
                Programme <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.program} 
                onValueChange={(value) => setFormData({ ...formData, program: value })}
                disabled={!formData.center}
              >
                <SelectTrigger className="mt-2 h-12 border-gray-200" data-testid="select-program">
                  <SelectValue placeholder={formData.center ? "Choisir un programme" : "Sélectionnez d'abord un centre"} />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((program) => (
                    <SelectItem key={program.id} value={program.id}>
                      {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Niveau d'études */}
            <div className="md:col-span-2">
              <Label className="text-[#003366] font-semibold">
                Niveau d'études actuel
              </Label>
              <Select 
                value={formData.education_level} 
                onValueChange={(value) => setFormData({ ...formData, education_level: value })}
              >
                <SelectTrigger className="mt-2 h-12 border-gray-200" data-testid="select-education">
                  <SelectValue placeholder="Sélectionner votre niveau" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6eme">6ème</SelectItem>
                  <SelectItem value="3eme">3ème / BEPC</SelectItem>
                  <SelectItem value="terminale">Terminale</SelectItem>
                  <SelectItem value="bac">Baccalauréat</SelectItem>
                  <SelectItem value="bac+2">Bac+2 / BTS / DUT</SelectItem>
                  <SelectItem value="bac+3">Bac+3 / Licence</SelectItem>
                  <SelectItem value="bac+4">Bac+4 / Maîtrise</SelectItem>
                  <SelectItem value="bac+5">Bac+5 / Master</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Motivation */}
            <div className="md:col-span-2">
              <Label htmlFor="motivation" className="text-[#003366] font-semibold">
                Lettre de motivation (optionnel)
              </Label>
              <Textarea
                id="motivation"
                value={formData.motivation}
                onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
                className="mt-2 min-h-[150px] border-gray-200 focus:border-[#003366] focus:ring-[#003366]/20"
                placeholder="Parlez-nous de votre projet professionnel et de vos motivations..."
                data-testid="textarea-motivation"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="rounded-full px-8"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full px-8 py-3 font-semibold shadow-lg"
              data-testid="submit-preinscription"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={18} />
                  Envoi en cours...
                </>
              ) : (
                'Soumettre ma préinscription'
              )}
            </Button>
          </div>
        </motion.form>

        {/* Info box */}
        <div className="mt-8 bg-blue-50 border border-blue-100 rounded-xl p-6">
          <h3 className="font-heading font-bold text-[#003366] mb-2">Que se passe-t-il après ?</h3>
          <ol className="text-gray-600 space-y-2 list-decimal list-inside">
            <li>Nous recevons votre demande et l'examinons sous 48h</li>
            <li>Un conseiller vous contacte pour confirmer votre inscription</li>
            <li>Vous recevez les informations sur les prochaines étapes (dossier, test, entretien)</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
